import { Box, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { statusColors } from "../../../constants/color-constants";
import { RouteConstants } from "../../../constants/route-constant";
import {
  UM_API_STATUS,
  UM_STATUS_FILTER_OPTIONS,
} from "../../../constants/um-constants";
import { useUmQueryList } from "../../../hooks/queries";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setUmQueriesCount } from "../../../store/providerReducer";
import {
  filterUmQueriesBySearch,
  filterUmQueriesByStatus,
  transformUmQueriesToRows,
} from "../../../utils/um-query-utils";
import type { UmQuery, UmQueryListRow } from "../../../types/um-queries";
import FilterComponent from "../../Filter";
import PaginationComponent from "../../Shared/Pagination";
import { ReusableTable } from "../../Shared/table";

const limit = 15;

export default function UmQueryTable() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const provider = useAppSelector((state) => state?.provider?.provider);
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const umQueriesCount = useAppSelector((state) => state?.provider?.umQueriesCount);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>([
    UM_API_STATUS.OPEN,
  ]);
  const [search, setSearch] = useState("");

  const listParams = useMemo(
    () => ({
      provider_npi: providerNPI ?? "",
    }),
    [providerNPI]
  );

  const {
    data: queryListResponse,
    isLoading,
    isFetching,
  } = useUmQueryList(listParams, {
    enabled: Boolean(providerNPI),
    polling: true,
  });

  const filteredRows = useMemo(() => {
    const rows = transformUmQueriesToRows(
      queryListResponse ?? { success: true, count: 0, queries: [] },
      provider?.timezone_info?.timezone_name
    );

    const statusFiltered = filterUmQueriesByStatus(rows, statusFilter);

    return filterUmQueriesBySearch(statusFiltered, search);
  }, [queryListResponse, provider?.timezone_info?.timezone_name, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));
  const paginatedData = useMemo(
    () => filteredRows.slice((page - 1) * limit, page * limit),
    [filteredRows, page]
  );

  useEffect(() => {
    if (!providerNPI || !queryListResponse) return;

    const isDefaultOpenFilter =
      statusFilter.length === 1 && statusFilter[0] === UM_API_STATUS.OPEN;

    if (isDefaultOpenFilter) {
      const openCount = (queryListResponse.queries ?? []).filter(
        (query: UmQuery) => query.status === UM_API_STATUS.OPEN
      ).length;

      if (openCount !== umQueriesCount) {
        dispatch(setUmQueriesCount(openCount));
      }
    }
  }, [dispatch, providerNPI, queryListResponse, statusFilter, umQueriesCount]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const columns = [
    {
      header: "Patient Name / MRN",
      align: "left" as const,
      accessor: (row: UmQueryListRow) => (
        <Tooltip
          arrowPosition="side"
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={
            <div>
              <div>{row.patientName}</div>
              <div>{row.patientMRN}</div>
            </div>
          }
          position="bottom-start"
          withArrow
          multiline
        >
          <div>
            <Text size={isSmallScreen ? "12px" : "14px"}>{row.patientName}</Text>
            <Text size="xs" c="dimmed" mt="2px">
              {row.patientMRN}
            </Text>
          </div>
        </Tooltip>
      ),
      width: "220px",
    },
    {
      header: "Status",
      align: "left" as const,
      accessor: "status" as keyof UmQueryListRow,
      width: "120px",
      cellType: "status" as const,
    },
    {
      header: "Ageing",
      align: "left" as const,
      accessor: "ageing" as keyof UmQueryListRow,
      width: "120px",
      cellType: "aging" as const,
    },
    {
      header: "Date",
      align: "left" as const,
      accessor: (row: UmQueryListRow) => (
        <Tooltip
          arrowPosition="side"
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={`${row.queryDate}, ${row.createdFormatTime}`}
          position="bottom-start"
          withArrow
        >
          <Text size={isSmallScreen ? "12px" : "14px"}>{row.queryDate}</Text>
        </Tooltip>
      ),
      width: "140px",
    },
    {
      header: "Query ID",
      align: "left" as const,
      accessor: "queryId" as keyof UmQueryListRow,
      width: "180px",
    },
  ];

  const handleRowClick = (row: UmQueryListRow) => {
    const fullQuery = queryListResponse?.queries?.find(
      (query: UmQuery) => query.id === row.id
    );

    navigate(
      RouteConstants.ROUTE_UM_QUERY_DETAILS.replace(":query_id", row.id),
      {
        state: {
          queryData: row,
          umQuery: fullQuery,
          from: RouteConstants.ROUTE_UM_QUERIES,
        },
      }
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box style={{ flexShrink: 0 }}>
        <FilterComponent
          loading={isLoading && !queryListResponse}
          refreshing={isFetching && !isLoading}
          dropDown1Options={UM_STATUS_FILTER_OPTIONS}
          dropDown2Options={[]}
          dropdown1Values={statusFilter}
          setDropdown1Values={setStatusFilter}
          dropdown2Values={[]}
          setDropdown2Values={() => undefined}
          hideSecondDropdown
          hideThirdDropdown
          searchValue={search}
          setSearchValue={setSearch}
          debounceDelay={500}
          dropdown1PlaceHolder="Select Status"
        />
      </Box>

      <ReusableTable
        loading={isLoading}
        data={paginatedData}
        columns={columns}
        getRowId={(row) => row.id}
        onRowClick={handleRowClick}
        statusColors={statusColors}
        showSeriesNumber
      />

      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          padding: "4px",
        }}
      >
        <PaginationComponent
          total={totalPages}
          value={page}
          onChange={setPage}
          disabled={isLoading}
        />
      </Box>
    </div>
  );
}
